import React, { useState, useRef, useEffect } from 'react';
import { AlertTriangle, Copy, Check, ChevronDown, ChevronUp, FileText, Shield, Globe, Download } from 'lucide-react';
import { displayToneFromParams } from '../../utils/toneMigration.js';

// ── Shared styles ───────────────────────────────────────────────────────────
const labelStyle = (t) => ({
  fontSize: '10px',
  fontFamily: "'JetBrains Mono', monospace",
  color: t.textMuted || '#7a7a8e',
  letterSpacing: '0.8px',
  textTransform: 'uppercase',
});

const valueStyle = (t) => ({
  fontSize: '13px',
  color: t.text || '#e0e0ee',
  fontWeight: 500,
});

// Map raw agent identifiers (e.g. "critic", "critic_node.web_search",
// "researcher_node.url_fetch") to a friendly Italian phase label.
const friendlyAgentLabel = (agent) => {
  if (!agent) return 'Sistema';
  if (agent.startsWith('critic')) return 'Verifica fatti';
  if (agent.startsWith('researcher')) return 'Ricerca';
  if (agent.startsWith('writer')) return 'Scrittura';
  return agent;
};

// Collapse multiple LANDING_REQUIRED_BLOCK_EMPTY warnings (one per missing
// field) into a single synthetic banner so the user sees ONE amber box
// listing all empty fields instead of N separate boxes. Non-landing
// warnings pass through unchanged. Preserves order: synthetic entry takes
// the position of the FIRST consolidated warning, rest stays in place.
const consolidateLandingWarnings = (warnings) => {
  if (!Array.isArray(warnings) || warnings.length === 0) return warnings;
  let firstReqIdx = -1;
  const fields = [];
  const rest = [];
  warnings.forEach((w, i) => {
    if (w?.code === 'LANDING_REQUIRED_BLOCK_EMPTY') {
      if (firstReqIdx === -1) firstReqIdx = i;
      const block = w.details?.block;
      const field = w.details?.field;
      if (block && field) fields.push(`${block}.${field}`);
    } else {
      rest.push(w);
    }
  });
  if (fields.length === 0) return warnings;
  const synthetic = {
    code: 'LANDING_REQUIRED_BLOCK_EMPTY',
    agent: 'curator.landing',
    severity: 'warning',
    message: '',
    details: { fields },
  };
  // Insert synthetic at the position of the first consolidated warning.
  const out = [...rest];
  const insertAt = Math.min(firstReqIdx, out.length);
  out.splice(insertAt, 0, synthetic);
  return out;
};

// Translate a backend warning into non-technical Italian copy keyed by `code`.
// Returns `{ title, message, urls? }`. Falls back to the raw backend message.
const friendlyWarningCopy = (warning) => {
  const code = warning.code;
  const agent = warning.agent || '';
  const isCritic = agent.startsWith('critic');
  const failedUrls = warning.details?.failed_urls || [];
  const totalRequested = warning.details?.total_requested ?? failedUrls.length;
  const totalSucceeded = warning.details?.total_succeeded ?? 0;

  if (code === 'REQUEST_URL_FETCH_FAILED') {
    const failed = failedUrls.length || (totalRequested - totalSucceeded);
    return {
      title: 'Alcuni link non sono stati letti',
      message: `Non siamo riusciti a leggere ${failed} ${failed === 1 ? 'link' : 'link'} su ${totalRequested} che hai fornito. Il contenuto è stato comunque generato con gli altri link disponibili.`,
      urls: failedUrls,
    };
  }

  if (code === 'WEB_SEARCH_FAILED') {
    return isCritic
      ? {
          title: 'Verifica fatti parziale',
          message: 'Le affermazioni sono state verificate solo con le informazioni già disponibili: la ricerca online non era utilizzabile in questo momento. Ti consigliamo di rileggere il testo prima di pubblicarlo.',
        }
      : {
          title: 'Ricerca web non disponibile',
          message: 'Non è stato possibile fare ricerche aggiuntive sul web. Il contenuto è stato creato usando solo la knowledge base e i link che hai fornito.',
        };
  }

  if (code === 'LLM_SERVICE_UNAVAILABLE') {
    return isCritic
      ? {
          title: 'Verifica fatti saltata',
          message: 'La verifica automatica delle affermazioni è stata saltata per un problema temporaneo del servizio. Rileggi il testo con attenzione prima di pubblicarlo.',
        }
      : {
          title: 'Una fase è stata saltata',
          message: 'Una parte della pipeline è stata saltata per un problema temporaneo del servizio. Il contenuto potrebbe essere meno ricco del solito.',
        };
  }

  if (code === 'LANDING_REQUIRED_BLOCK_EMPTY') {
    const fields = warning.details?.fields || [];
    if (fields.length === 0) {
      return {
        title: 'Campi obbligatori vuoti',
        message: 'La landing ha campi obbligatori vuoti. Rigenera per completarli.',
      };
    }
    return {
      title: 'Campi obbligatori vuoti',
      message: `La landing ha ${fields.length === 1 ? 'un campo obbligatorio vuoto' : `${fields.length} campi obbligatori vuoti`}: ${fields.join(', ')}. Rigenera per completare la landing.`,
    };
  }

  if (code === 'LANDING_OPERATOR_SOCIAL_PROOF_DROPPED') {
    const op = String(warning.details?.operator_input || '').slice(0, 200).trim();
    return {
      title: 'Suggerimento dal brief',
      message: op
        ? `Il brief suggeriva ulteriore social proof («${op}») non presente in questa bozza. Considera di rigenerare o aggiungere manualmente le bullets.`
        : 'Il brief suggeriva ulteriore social proof non presente in questa bozza. Considera di rigenerare o aggiungere manualmente le bullets.',
    };
  }

  // Fallback: surface whatever message the backend sent, with a generic title.
  return {
    title: `${friendlyAgentLabel(agent)} — Avviso`,
    message: warning.message || 'Si è verificato un avviso durante la generazione.',
  };
};

const WarningBanner = ({ warning, t }) => {
  const { title, message, urls } = friendlyWarningCopy(warning);

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '10px',
      padding: '12px 16px', borderRadius: '8px',
      background: 'rgba(245,158,11,0.08)',
      border: '1px solid rgba(245,158,11,0.25)',
    }}>
      <AlertTriangle size={16} color="#F59E0B" style={{ flexShrink: 0, marginTop: '1px' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '11px', fontWeight: 700, color: '#F59E0B',
          fontFamily: "'JetBrains Mono', monospace",
          textTransform: 'uppercase', letterSpacing: '0.5px',
          marginBottom: '4px',
        }}>
          {title}
        </div>
        <div style={{ fontSize: '13px', color: t.text || '#e0e0ee', lineHeight: 1.5 }}>
          {message}
        </div>
        {urls?.length > 0 && (
          <ul style={{
            margin: '8px 0 0 0', padding: 0, listStyle: 'none',
            display: 'flex', flexDirection: 'column', gap: '4px',
          }}>
            {urls.map((u, i) => (
              <li
                key={i}
                style={{
                  fontSize: '11px', color: t.textMuted || '#7a7a8e',
                  fontFamily: "'JetBrains Mono', monospace",
                  wordBreak: 'break-all', lineHeight: 1.4,
                }}
                title={u.error}
              >
                • {u.url}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const CLAIM_STATUS = {
  verified:    { bg: 'rgba(16,163,127,0.1)',  color: '#10A37F', label: 'Verificato',      icon: '✓' },
  approximate: { bg: 'rgba(245,158,11,0.1)',  color: '#F59E0B', label: 'Approssimato',    icon: '≈' },
  unverified:  { bg: 'rgba(245,158,11,0.1)',  color: '#F59E0B', label: 'Non verificato',  icon: '?' },
  false:       { bg: 'rgba(239,68,68,0.1)',   color: '#EF4444', label: 'Falso',            icon: '✕' },
  kb_source:   { bg: 'rgba(59,130,246,0.1)',  color: '#3B82F6', label: 'KB',               icon: '◆' },
  task_source: { bg: 'rgba(59,130,246,0.1)',  color: '#3B82F6', label: 'Task',             icon: '◆' },
};

const CREDIBILITY_COLORS = {
  high:   '#10A37F',
  medium: '#F59E0B',
  low:    '#EF4444',
};

const SOURCE_TYPE_LABELS = {
  web_search:     { label: 'Web',      color: '#8B5CF6' },
  kb_web:         { label: 'KB Web',   color: '#3B82F6' },
  kb_docs:        { label: 'KB Docs',  color: '#3B82F6' },
  request_url:    { label: 'URL',      color: '#10A37F' },
  upload_bundle:  { label: 'Upload',   color: '#F59E0B' },
  input_url:      { label: 'URL',      color: '#10A37F' },
};

// ── Metadata field ──────────────────────────────────────────────────────────
const MetaField = ({ label, value, t }) => {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={labelStyle(t)}>{label}</span>
      <span style={valueStyle(t)}>{value}</span>
    </div>
  );
};

// ── Collapsible section wrapper ─────────────────────────────────────────────
const CollapsibleSection = ({ title, icon, count, badges, defaultOpen, color, w, t, children }) => {
  const [expanded, setExpanded] = useState(defaultOpen ?? true);
  return (
    <div style={{ borderRadius: '10px', border: `1px solid ${w(0.06)}`, overflow: 'hidden' }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px',
          background: w(0.02), cursor: 'pointer', userSelect: 'none',
        }}
      >
        {icon}
        <span style={{ fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", color, fontWeight: 600 }}>
          {title}{count != null ? ` (${count})` : ''}
        </span>
        {badges}
        <span style={{ marginLeft: 'auto' }}>
          {expanded ? <ChevronUp size={14} color={w(0.4)} /> : <ChevronDown size={14} color={w(0.4)} />}
        </span>
      </div>
      {expanded && children}
    </div>
  );
};

// ── Claims table ────────────────────────────────────────────────────────────
const ClaimsTable = ({ claims, summary, confidence, color, w, t }) => {
  if (!claims?.length) return null;

  const summaryBadges = (
    <span style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
      {summary?.task_source > 0 && <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: 'rgba(59,130,246,0.1)', color: '#3B82F6', fontFamily: "'JetBrains Mono', monospace" }}>◆ {summary.task_source} task</span>}
      {summary?.verified > 0 && <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: 'rgba(16,163,127,0.1)', color: '#10A37F', fontFamily: "'JetBrains Mono', monospace" }}>✓ {summary.verified}</span>}
      {summary?.unverified > 0 && <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: 'rgba(245,158,11,0.1)', color: '#F59E0B', fontFamily: "'JetBrains Mono', monospace" }}>? {summary.unverified}</span>}
      {summary?.false_count > 0 && <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: 'rgba(239,68,68,0.1)', color: '#EF4444', fontFamily: "'JetBrains Mono', monospace" }}>✕ {summary.false_count}</span>}
      {confidence != null && <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: confidence >= 70 ? 'rgba(16,163,127,0.1)' : 'rgba(245,158,11,0.1)', color: confidence >= 70 ? '#10A37F' : '#F59E0B', fontFamily: "'JetBrains Mono', monospace" }}>{confidence}%</span>}
    </span>
  );

  return (
    <CollapsibleSection
      title="VERIFICA CLAIM"
      icon={<Shield size={13} color={color} strokeWidth={1.5} />}
      count={claims.length}
      badges={summaryBadges}
      defaultOpen={true}
      color={color} w={w} t={t}
    >
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: "'JetBrains Mono', monospace" }}>
          <thead>
            <tr style={{ background: w(0.03) }}>
              {['#', 'Claim', 'Stato', 'Fonte', 'Correzione'].map(h => (
                <th key={h} style={{
                  padding: '8px 12px', textAlign: 'left', fontSize: '10px',
                  color: t.textMuted || '#7a7a8e', fontWeight: 600,
                  letterSpacing: '0.5px', textTransform: 'uppercase',
                  borderBottom: `1px solid ${w(0.06)}`,
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {claims.map((claim, i) => {
              const st = CLAIM_STATUS[claim.status] || CLAIM_STATUS.unverified;
              return (
                <tr key={i} style={{ borderBottom: `1px solid ${w(0.04)}` }}>
                  <td style={{ padding: '8px 12px', color: t.textMuted || '#7a7a8e', width: '30px' }}>{i + 1}</td>
                  <td style={{ padding: '8px 12px', color: t.text || '#e0e0ee', maxWidth: '320px', lineHeight: 1.4 }}>
                    {claim.claim}
                  </td>
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                      background: st.bg, color: st.color,
                    }}>
                      {st.icon} {st.label}
                    </span>
                  </td>
                  <td style={{ padding: '8px 12px', color: t.textMuted || '#7a7a8e', maxWidth: '220px', fontSize: '11px' }}>
                    {claim.sourceFound || '—'}
                  </td>
                  <td style={{ padding: '8px 12px', color: claim.correctionInContext ? '#F59E0B' : (t.textFaint || '#555'), fontSize: '11px', fontStyle: claim.correctionInContext ? 'italic' : 'normal' }}>
                    {claim.correctionInContext || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </CollapsibleSection>
  );
};

// ── Research sources table ───────────────────────────────────────────────────
const SourcesTable = ({ research, sources, color, w, t }) => {
  // V2: use top-level sources.from_research (programmatic, verified)
  // Legacy: fall back to research.sources (LLM-generated, may contain fabrications)
  const isV2 = !!research?.executive_summary;
  const sourceList = isV2
    ? (sources?.from_research || [])
    : (research?.sources || []);

  if (!sourceList.length) return null;

  // V2: extracted_passages replaces key_facts
  const passages = isV2
    ? (research?.extracted_passages || [])
    : null;
  const keyFacts = !isV2
    ? (research?.key_facts || [])
    : null;

  return (
    <CollapsibleSection
      title="FONTI DI RICERCA"
      icon={<Globe size={13} color={color} strokeWidth={1.5} />}
      count={sourceList.length}
      defaultOpen={true}
      color={color} w={w} t={t}
    >
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: "'JetBrains Mono', monospace" }}>
          <thead>
            <tr style={{ background: w(0.03) }}>
              {(isV2 ? ['Fonte', 'Tipo', 'Data'] : ['Fonte', 'Credibilità', 'Data']).map(h => (
                <th key={h} style={{
                  padding: '8px 12px', textAlign: 'left', fontSize: '10px',
                  color: t.textMuted || '#7a7a8e', fontWeight: 600,
                  letterSpacing: '0.5px', textTransform: 'uppercase',
                  borderBottom: `1px solid ${w(0.06)}`,
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sourceList.map((src, i) => {
              if (isV2) {
                // V2: sources.from_research shape
                const st = SOURCE_TYPE_LABELS[src.source_type] || { label: src.source_type || '—', color: '#7a7a8e' };
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${w(0.04)}` }}>
                    <td style={{ padding: '8px 12px', maxWidth: '400px' }}>
                      <div style={{ color: t.text || '#e0e0ee', fontSize: '12px', lineHeight: 1.4 }}>{src.title || '—'}</div>
                      {src.url && <div style={{ fontSize: '10px', color: t.textFaint || '#555', marginTop: '2px', wordBreak: 'break-all' }}>{src.url}</div>}
                    </td>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                        background: `${st.color}15`, color: st.color,
                      }}>
                        {st.label}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: t.textMuted || '#7a7a8e', fontSize: '11px', whiteSpace: 'nowrap' }}>
                      {src.fetched_at ? new Date(src.fetched_at).toLocaleDateString('it-IT') : '—'}
                    </td>
                  </tr>
                );
              } else {
                // Legacy: research.sources shape
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${w(0.04)}` }}>
                    <td style={{ padding: '8px 12px', maxWidth: '400px' }}>
                      <div style={{ color: t.text || '#e0e0ee', fontSize: '12px', lineHeight: 1.4 }}>{src.title}</div>
                      {src.url && <div style={{ fontSize: '10px', color: t.textFaint || '#555', marginTop: '2px', wordBreak: 'break-all' }}>{src.url}</div>}
                    </td>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                        background: `${CREDIBILITY_COLORS[src.credibility] || '#7a7a8e'}15`,
                        color: CREDIBILITY_COLORS[src.credibility] || '#7a7a8e',
                      }}>
                        {src.credibility || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: t.textMuted || '#7a7a8e', fontSize: '11px', whiteSpace: 'nowrap' }}>
                      {src.date_accessed || '—'}
                    </td>
                  </tr>
                );
              }
            })}
          </tbody>
        </table>
      </div>

      {/* V2: Extracted passages (replaces key_facts) */}
      {passages?.length > 0 && (
        <div style={{ padding: '10px 16px', borderTop: `1px solid ${w(0.06)}` }}>
          <div style={{ ...labelStyle(t), marginBottom: '6px' }}>PASSAGGI ESTRATTI</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {passages.map((p, i) => (
              <div key={i} style={{ padding: '8px 12px', borderRadius: '6px', background: w(0.02), fontSize: '12px', lineHeight: 1.5, color: t.text || '#e0e0ee' }}>
                <span>{p.text}</span>
                <span style={{ display: 'flex', gap: '6px', marginTop: '4px', alignItems: 'center' }}>
                  <span style={{
                    fontSize: '10px', padding: '1px 6px', borderRadius: '3px',
                    background: p.validated ? 'rgba(16,163,127,0.1)' : 'rgba(245,158,11,0.1)',
                    color: p.validated ? '#10A37F' : '#F59E0B',
                    fontWeight: 600,
                  }}>
                    {p.validated ? '\u2713' : '?'} {p.category}
                  </span>
                  <span style={{ fontSize: '10px', color: t.textFaint || '#555' }}>
                    — fonte #{p.source_index}
                    {p.relevance && ` \u00b7 ${p.relevance}`}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legacy: Key facts */}
      {keyFacts?.length > 0 && (
        <div style={{ padding: '10px 16px', borderTop: `1px solid ${w(0.06)}` }}>
          <div style={{ ...labelStyle(t), marginBottom: '6px' }}>FATTI CHIAVE</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {keyFacts.map((kf, i) => (
              <div key={i} style={{ padding: '8px 12px', borderRadius: '6px', background: w(0.02), fontSize: '12px', lineHeight: 1.5, color: t.text || '#e0e0ee' }}>
                <span>{kf.fact}</span>
                <span style={{ display: 'block', marginTop: '2px', fontSize: '10px', color: t.textFaint || '#555' }}>— {kf.source}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
};


// ── Download dropdown menu ──────────────────────────────────────────────────
const DL_FORMATS = [
  { id: 'md',   label: 'Markdown (.md)',  icon: '📝' },
  { id: 'docx', label: 'Word (.doc)',     icon: '📄' },
  { id: 'pdf',  label: 'PDF (.pdf)',      icon: '📕' },
];

const DownloadMenu = ({ onSelect, onClose, containerRef, t }) => {
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, containerRef]);

  return (
    <div style={{
      position: 'absolute', top: '100%', right: 0, marginTop: '4px', zIndex: 100,
      background: t.inputBg || '#1a1a2e', border: `1px solid ${t.border}`,
      borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
      overflow: 'hidden', minWidth: '170px',
    }}>
      {DL_FORMATS.map(fmt => (
        <button
          key={fmt.id}
          onClick={() => onSelect(fmt.id)}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
            padding: '9px 14px', border: 'none', background: 'transparent',
            color: t.text, fontSize: '12px', fontWeight: 500, cursor: 'pointer',
            textAlign: 'left',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = t.surface || 'rgba(255,255,255,0.04)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <span style={{ fontSize: '14px' }}>{fmt.icon}</span>
          {fmt.label}
        </button>
      ))}
    </div>
  );
};

// ── Single history entry ────────────────────────────────────────────────────
const HistoryEntry = ({ entry, copiedId, onCopy, onGetText, onDownload, renderEntry, color, w, t }) => {
  const [dlOpen, setDlOpen] = useState(false);
  const dlRef = useRef(null);
  const isCarousel = entry.results?.contentFormat === 'carousel';
  const isNewsletter = entry.results?.contentFormat === 'newsletter';
  const isInstagram = entry.results?.platform === 'instagram';
  const entryColor = isCarousel ? '#6366F1'
    : isNewsletter ? '#0A66C2'
    : isInstagram ? '#E4405F'
    : (entry.modeConfig?.color || color);
  const r = entry.results || {};
  const isCancelledEntry = r.status === 'cancelled' || r.cancelled;
  const ts = new Date(entry.timestamp);

  const reviewId = entry.id
    ? entry.id.substring(0, 8)
    : ts.toISOString().replace(/[-:T]/g, '').substring(0, 14);

  // Get claims from the latest review iteration
  const latestReview = r.reviews?.length > 0 ? r.reviews[r.reviews.length - 1] : null;
  const claims = latestReview?.claims;
  const claimsSummary = latestReview?.summary;
  const claimsConfidence = latestReview?.overall_confidence;

  // Metadata from new structure
  const iterations = r.metadata?.iterations;

  return (
    <div style={{
      background: t.surface, borderRadius: '14px',
      border: `1px solid ${t.border}`, overflow: 'hidden',
    }}>

      {/* ── Section 1: Review Header ── */}
      <div style={{
        padding: '16px 20px', borderBottom: `1px solid ${t.border}`,
        display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
      }}>
        <FileText size={16} color={entryColor} />
        <span style={{
          fontSize: '14px', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
          color: t.text || '#e0e0ee',
        }}>
          Content Review
        </span>
        <span style={{
          fontSize: '11px', fontFamily: "'JetBrains Mono', monospace",
          color: t.textMuted || '#7a7a8e',
        }}>
          {reviewId}
        </span>
        <span style={{
          padding: '3px 10px', borderRadius: '6px',
          background: `${entryColor}20`, color: entryColor,
          fontSize: '11px', fontWeight: 700,
        }}>
          {isCarousel ? 'Carousel' : isNewsletter ? 'LinkedIn Newsletter' : isInstagram ? 'Instagram' : (entry.modeConfig?.name || entry.mode)}
        </span>

        {/* Action buttons */}
        {!isCancelledEntry && (
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', position: 'relative' }}>
          <button
            onClick={() => onCopy(onGetText(entry), 'all-' + entry.id)}
            style={{
              padding: '6px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
              cursor: 'pointer', background: t.surface, border: `1px solid ${t.border}`,
              color: t.textMuted, display: 'flex', alignItems: 'center', gap: '5px',
            }}
          >
            {copiedId === 'all-' + entry.id
              ? <><Check size={12} /> Copiato</>
              : <><Copy size={12} /> Copia tutto</>}
          </button>
          <div ref={dlRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setDlOpen(o => !o)}
              style={{
                padding: '6px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                cursor: 'pointer', background: t.surface, border: `1px solid ${t.border}`,
                color: t.textMuted, display: 'flex', alignItems: 'center', gap: '5px',
              }}
            >
              <Download size={12} /> Scarica <ChevronDown size={10} />
            </button>
            {dlOpen && (
              <DownloadMenu
                onSelect={(fmt) => { onDownload(entry, fmt); setDlOpen(false); }}
                onClose={() => setDlOpen(false)}
                containerRef={dlRef}
                t={t}
              />
            )}
          </div>
        </div>
        )}
      </div>

      {/* ── Section 1b: Content (always visible, at top) ── */}
      <div style={{ padding: '20px', borderBottom: `1px solid ${t.border}` }}>
        {renderEntry(entry)}
      </div>

      {/* ── Section 1c: Warnings ── */}
      {(r.warnings ?? []).length > 0 && (
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${t.border}`,
          display: 'flex', flexDirection: 'column', gap: '8px',
        }}>
          {consolidateLandingWarnings(r.warnings ?? []).map((warning, i) => (
            <WarningBanner key={`${warning.code || warning.agent || 'w'}-${i}`} warning={warning} t={t} />
          ))}
        </div>
      )}

      {/* ── Section 2: Metadata Grid ── */}
      <div style={{
        padding: '14px 20px', borderBottom: `1px solid ${t.border}`,
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: '12px',
      }}>
        <MetaField label="Creato" value={ts.toLocaleString('it-IT', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })} t={t} />
        <MetaField label="Piattaforma" value={r.platform || entry.params?.platform} t={t} />
        <MetaField label="Obiettivo" value={entry.params?.objective} t={t} />
        <MetaField label="Persona" value={entry.params?.persona} t={t} />
        <MetaField label="Tono" value={displayToneFromParams(entry.params)} t={t} />
        {entry.mode === 'social' && entry.params?.socialChannel && (
          <MetaField label="Canale" value={entry.params.socialChannel} t={t} />
        )}
        {entry.generationDuration && (
          <MetaField label="Durata" value={`${entry.generationDuration}s`} t={t} />
        )}
        {r.factualConfidence != null && (
          <MetaField
            label="Fact-check"
            value={
              (r.warnings ?? []).some(w =>
                (w.agent || '').startsWith('critic') &&
                w.code === 'LLM_SERVICE_UNAVAILABLE'
              )
                ? 'N/A (saltato)'
                : `${r.factualConfidence}/100`
            }
            t={t}
          />
        )}
        {r.styleScore != null && (
          <MetaField label="Style" value={`${r.styleScore}/100`} t={t} />
        )}
        {iterations != null && (
          <MetaField label="Iterazioni" value={`${iterations}`} t={t} />
        )}
      </div>

      {/* ── Section 3: Topic ── */}
      {r.topic && (
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${t.border}` }}>
          <span style={{ ...labelStyle(t), display: 'block', marginBottom: '4px' }}>Topic</span>
          <span style={{ fontSize: '13px', color: t.text || '#e0e0ee', lineHeight: 1.5 }}>
            {r.topic}
          </span>
        </div>
      )}

      {/* ── Section 4: Claims Verification (from reviews) ── */}
      {claims?.length > 0 && (
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${t.border}` }}>
          <ClaimsTable
            claims={claims}
            summary={claimsSummary}
            confidence={claimsConfidence}
            color={entryColor}
            w={w}
            t={t}
          />
        </div>
      )}

      {/* ── Section 5: Research Sources ── */}
      {(r.sources?.from_research?.length > 0 || r.research?.sources?.length > 0 || r.research?.extracted_passages?.length > 0) && (
        <div style={{ padding: '16px 20px' }}>
          <SourcesTable
            research={r.research}
            sources={r.sources}
            color={entryColor}
            w={w}
            t={t}
          />
        </div>
      )}

    </div>
  );
};

// ── Main component ──────────────────────────────────────────────────────────
const Step3History = ({ state, onRegenerate, onConfirm, onDelete, onCopy, onGetText, onGetHtml, onDownload, renderEntry, color, w, t }) => {
  const { outputHistory, copiedId } = state;
  const c = color;

  if (outputHistory.length === 0) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', background: t.surface, borderRadius: '14px', border: `1px solid ${t.border}` }}>
        <div style={{ fontSize: '15px', color: t.textMuted }}>Nessun output ancora. Vai a Genera e crea il primo contenuto.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {outputHistory.map(entry => (
        <HistoryEntry
          key={entry.id}
          entry={entry}
          copiedId={copiedId}
          onCopy={onCopy}
          onGetText={onGetText}
          onDownload={onDownload}
          renderEntry={renderEntry}
          color={c}
          w={w}
          t={t}
        />
      ))}
    </div>
  );
};

export default Step3History;
